import { MfaType } from "@/api/openapi";
import { translate } from "@/config/translator";
import {
  Popover,
  PopoverSurface,
  PopoverTrigger,
} from "@fluentui/react-components";
import {
  faEllipsis,
  faEnvelope,
  faMobile,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { clsx } from "clsx/lite";
import { ReactNode } from "react";
import { useMfaMethodItem } from "./mfaMethodItem/useMfaMethodItem";

type MfaMethodItemProps = {
  type: MfaType;
  label: ReactNode;
  onClick?: () => void;
  selected?: boolean;
  disabled?: boolean;
  primary?: boolean;
  active?: boolean;
  popoverChildren?: ReactNode;
  popoverOpen?: boolean;
  onChangePopoverOpen?: (open: boolean) => void;
};

export function MfaMethodItem({
  type,
  label,
  onClick,
  selected,
  disabled,
  primary,
  active,
  popoverChildren,
  popoverOpen,
  onChangePopoverOpen,
}: MfaMethodItemProps) {
  const { lang } = useMfaMethodItem();

  return (
    <button
      className={clsx(
        "p-4",
        "rounded-sm",
        "border",
        "border-neutral",
        "flex",
        "flex-row",
        "gap-3",
        "justify-start",
        "items-center",
        "w-full",
        onClick && !disabled && "cursor-pointer",
        selected && "bg-primary-50 font-bold text-primary",
        disabled && "opacity-50",
      )}
      onClick={disabled ? undefined : onClick}
    >
      <div
        className={clsx(
          "p-1",
          "bg-neutral-200",
          "flex",
          "justify-center",
          "items-center",
          "rounded-full",
          "aspect-square",
        )}
      >
        <FontAwesomeIcon
          className={clsx(
            "text-xl",
            selected ? "text-primary" : "text-font-secondary",
          )}
          icon={type === "totp" ? faMobile : faEnvelope}
        />
      </div>
      <div className="flex flex-row justify-between w-full items-center">
        <div className="flex flex-col items-start">
          <div className="flex flex-row gap-2 items-center">
            <span className="text-left">{label}</span>
            {primary && (
              <span className="rounded-full bg-neutral text-font-secondary px-3 py-px text-xs">
                {translate("primaryMethod", lang)}
              </span>
            )}
          </div>
          {active !== undefined && (
            <div className={active ? "text-green-500" : "text-warning"}>
              {active
                ? translate("enabled", lang)
                : translate("notEnabled", lang)}
            </div>
          )}
        </div>
        {popoverChildren && onChangePopoverOpen && (
          <Popover
            onOpenChange={(_, data) => onChangePopoverOpen(data.open)}
            open={popoverOpen}
          >
            <PopoverTrigger disableButtonEnhancement>
              <FontAwesomeIcon
                icon={faEllipsis}
                className="text-font-secondary text-3xl cursor-pointer"
              />
            </PopoverTrigger>

            <PopoverSurface>{popoverChildren}</PopoverSurface>
          </Popover>
        )}
      </div>
    </button>
  );
}
